import { isNative } from './isNative';

/**
 * `sessionStorage` (usato su web per i token) non è condiviso tra schede del browser.
 * `window.open` su altra scheda = nuovo context senza sessione → redirect al login.
 *
 * Prima di aprire la nuova scheda copiamo un handoff in `localStorage` (cancellato
 * al primo avvio in `consumeNewTabSessionHandoff` da AuthContext). Finestra
 * d’esposizione breve, solo stesso dispositivo / stesso profilo browser.
 */
const HANDOFF_KEY = 'cantir.openTabHandoff';

const ACCESS_KEY = 'cantir.accessToken';
const REFRESH_KEY = 'cantir.refreshToken';
const TENANT_KEY = 'cantir.tenantId';

const MAX_AGE_MS = 120_000;

function writeHandoffFromCurrentSessionStorage(): void {
  if (typeof window === 'undefined' || isNative()) return;
  try {
    const accessToken = sessionStorage.getItem(ACCESS_KEY);
    const refreshToken = sessionStorage.getItem(REFRESH_KEY);
    const tenantId = sessionStorage.getItem(TENANT_KEY);
    if (!accessToken && !refreshToken) return;
    localStorage.setItem(
      HANDOFF_KEY,
      JSON.stringify({
        accessToken,
        refreshToken,
        tenantId,
        ts: Date.now(),
      }),
    );
  } catch {
    // storage full / private mode
  }
}

/**
 * Esegue all’avvio dell’app in una scheda: copia l’handoff in `sessionStorage` così
 * allineato a `SecureStorage` web, poi rimuove `localStorage`.
 */
export function consumeNewTabSessionHandoff(): void {
  if (typeof window === 'undefined' || isNative()) return;
  let raw: string | null;
  try {
    raw = localStorage.getItem(HANDOFF_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  try {
    localStorage.removeItem(HANDOFF_KEY);
  } catch {
    // ignore
  }
  try {
    const p = JSON.parse(raw) as {
      accessToken: string | null;
      refreshToken: string | null;
      tenantId: string | null;
      ts: number;
    };
    if (!p.ts || Date.now() - p.ts > MAX_AGE_MS) return;
    if (p.accessToken) sessionStorage.setItem(ACCESS_KEY, p.accessToken);
    if (p.refreshToken) sessionStorage.setItem(REFRESH_KEY, p.refreshToken);
    if (p.tenantId) sessionStorage.setItem(TENANT_KEY, p.tenantId);
  } catch {
    // bad JSON
  }
}

/**
 * Apre un percorso (stesso sito) in una nuova scheda mantenendo la sessione su web.
 * Passare path assoluto dalla root, es. `/app/worksites/…/delivery-notes/new`.
 */
export function openAppPathInNewTab(path: string): void {
  if (typeof window === 'undefined') return;
  writeHandoffFromCurrentSessionStorage();
  window.open(path, '_blank', 'noopener,noreferrer');
}
